import { useState } from 'react'
import { ApiError } from '../../lib/api'
import type { AdminRequest } from '../../lib/adminApi'
import { formatTimestamp } from '../../lib/datetime'
import type { AdminOverview, SyncRun, SyncResponse } from '../../types/protocol'

interface Props {
  overview: AdminOverview
  request: AdminRequest
  onSynced: () => void
}

// The sync is one synchronous request — a multi-MB fetch plus ~3k upserts, so
// 5-30 seconds of spinner is normal and the 12h rate limit is what stops
// someone hammering Sleeper from the draft-night laptop.
export default function SyncPanel({ overview, request, onSynced }: Props) {
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SyncRun | null>(null)

  const blockedUntil = overview.next_sync_available_at

  async function sync(force: boolean) {
    if (force && !window.confirm('Force a sync now? This re-fetches the whole Sleeper dump.')) return

    setSyncing(true)
    setError(null)
    setResult(null)
    try {
      const response = await request<SyncResponse>(`/players/sync${force ? '?force=true' : ''}`, {
        method: 'POST',
      })
      setResult(response.sync_run)
      onSynced()
    } catch (e) {
      // SleeperSync::Error messages name the actual failure (HTTP status,
      // timeout, bad JSON) — show them as-is rather than "something failed".
      setError(e instanceof ApiError ? e.message : 'The sync request did not complete')
    } finally {
      setSyncing(false)
    }
  }

  return (
    <section className="panel p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="eyebrow">Sleeper sync</h2>
          <p className="mt-1 text-sm text-ink-mid">
            {overview.last_sync ? (
              <>
                Last run <SyncStatusBadge status={overview.last_sync.status} />{' '}
                {formatTimestamp(overview.last_sync.finished_at ?? overview.last_sync.started_at)}
                {overview.last_sync.players_upserted !== null && (
                  <> · {overview.last_sync.players_upserted} players upserted</>
                )}
              </>
            ) : (
              'Never synced on this server.'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void sync(false)}
            disabled={syncing || blockedUntil !== null}
            title={blockedUntil ? `Rate limited until ${formatTimestamp(blockedUntil)}` : undefined}
            className="rounded bg-signal px-4 py-2 text-sm font-semibold text-ink hover:bg-signal-dim disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
          <button
            type="button"
            onClick={() => void sync(true)}
            disabled={syncing}
            className="rounded border border-line-strong px-4 py-2 text-sm font-semibold text-ink-hi hover:bg-surface-3 disabled:opacity-50"
          >
            Force sync
          </button>
        </div>
      </div>

      {syncing && (
        <p className="mt-4 flex items-center gap-2 text-sm text-ink-mid">
          <span
            aria-hidden
            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-line-strong border-t-signal"
          />
          Fetching the Sleeper dump and upserting players. This usually takes 5–30 seconds.
        </p>
      )}

      {blockedUntil && !syncing && (
        <p className="mt-4 text-sm text-ink-lo">
          Players were synced in the last 12 hours, so “Sync now” is disabled until{' '}
          {formatTimestamp(blockedUntil)}. Use “Force sync” if you need one anyway.
        </p>
      )}

      {result && (
        <>
          <p className="mt-4 text-sm text-signal">
            Synced {result.players_upserted} players · {result.players_total_after} total in the
            table.
          </p>
          {/* A run can succeed and still have degraded: the bye-week schedule
              endpoint is undocumented, and when it fails the players upsert
              anyway. error_message carries the reason on a green run, and
              showing it here is what keeps that from being silent until
              somebody scans the history table. */}
          {result.error_message && (
            <p className="mt-1 text-sm text-warn">{result.error_message}</p>
          )}
        </>
      )}
      {error && <p className="mt-4 text-sm text-crit">{error}</p>}

      <h3 className="mt-6 text-sm font-semibold text-ink-mid">Recent runs</h3>
      {overview.recent_syncs.length === 0 ? (
        <p className="mt-2 text-sm text-ink-lo">No runs recorded yet.</p>
      ) : (
        <table className="mt-2 w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-ink-lo">
            <tr>
              <th className="py-1 pr-3 font-medium">Started</th>
              <th className="py-1 pr-3 font-medium">Source</th>
              <th className="py-1 pr-3 font-medium">Status</th>
              <th className="py-1 pr-3 font-medium">Upserted</th>
              <th className="py-1 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {overview.recent_syncs.map((run) => (
              <tr key={run.id} className="border-t border-line">
                <td className="py-1.5 pr-3 text-ink-mid">{formatTimestamp(run.started_at)}</td>
                <td className="py-1.5 pr-3 text-ink-mid">{run.source}</td>
                <td className="py-1.5 pr-3">
                  <SyncStatusBadge status={run.status} />
                </td>
                <td className="py-1.5 pr-3 text-ink-mid">{run.players_upserted ?? '—'}</td>
                <td className="py-1.5 text-ink-lo">{run.error_message ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

const STATUS_STYLES: Record<SyncRun['status'], string> = {
  success: 'bg-signal/10 text-signal',
  failed: 'bg-crit/10 text-crit',
  running: 'bg-surface-3 text-ink-mid',
}

function SyncStatusBadge({ status }: { status: SyncRun['status'] }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>
      {status}
    </span>
  )
}
